import { memo, useCallback, useMemo } from 'react';

import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { Box, Typography } from '@mui/material';

import CheckboxMultipleOutline from '@/assets/icons/CheckboxMultipleOutline';
import ClearAllButton from '@/components/ClearAllButton';
import type { SymbolKey } from '@/entities/symbol';
import { NameInput } from '@/components/NameInput';
import { DataHubCard } from '@/features/DataHub/components/DataHubCard';
import { ItemDetails } from '@/features/DataHub/components/ItemDetails';
import PreviewAccordion from '@/features/DataHub/components/Preview/PreviewAccordion';
import {
  useExportMeta,
  useExportNameMeta,
  useExportSettings
} from '@/features/DataHub/modules/Export/context/exportContexts';
import { ExportTypes } from '@/features/DataHub/modules/Export/types';
import useSelectionFunctions from '@/features/SymbolSelector/hooks/useSelectionFunctions';

const EXPORT_NAME_MAX_LENGTH = 25;
const EXPORT_NAME_MIN_WIDTH_CH = 14;
const CARET_BUFFER_CH = 1;

interface ItemDetailsContentProps {
  symbols: SymbolKey[];
  exportType: ExportTypes;
  onClearAll: () => void;
}

const ItemDetailsContent = memo(({ symbols, exportType, onClearAll }: ItemDetailsContentProps) => {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '16px'
      }}
    >
      {' '}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}
      >
        <Typography
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px'
          }}
        >
          <CheckboxMultipleOutline /> {symbols.length} item{symbols.length === 1 ? '' : 's'}{' '}
          selected
        </Typography>
        <ClearAllButton onClick={onClearAll} />
      </Box>
      <ItemDetails symbols={symbols} />
      {exportType === ExportTypes.DATA && <PreviewAccordion />}
    </Box>
  );
});

const ItemDetailsSection = () => {
  const { uniqueSymbols } = useExportMeta();
  const { exportName, setExportName } = useExportNameMeta();
  const { removeSymbolsFromSelection } = useSelectionFunctions();
  const symbols = useMemo(() => Array.from(uniqueSymbols), [uniqueSymbols]);
  const maxInputWidthCh = EXPORT_NAME_MAX_LENGTH + CARET_BUFFER_CH;

  const handleClearAllClick = useCallback(() => {
    removeSymbolsFromSelection(symbols);
  }, [removeSymbolsFromSelection, symbols]);
  const { exportSettings } = useExportSettings();
  return (
    <DataHubCard
      expandable={false}
      title={
        <>
          <Box
            component='span'
            sx={{
              display: 'inline-flex',
              width: 'fit-content',
              '& .name-input': {
                flex: 'none',
                width: 'fit-content'
              },
              '& .name-input .MuiInputBase-formControl': {
                width: 'auto'
              },
              '& .name-input .MuiInputBase-input': {
                fieldSizing: 'content',
                minWidth: `${EXPORT_NAME_MIN_WIDTH_CH}ch`,
                maxWidth: `${maxInputWidthCh}ch`
              }
            }}
          >
            <NameInput
              isEmpty={exportName === ''}
              maxLength={EXPORT_NAME_MAX_LENGTH}
              size={18}
              value={exportName ?? undefined}
              onChange={value => {
                setExportName(value);
              }}
            />
          </Box>
          <EditOutlinedIcon sx={{ fontSize: '16px' }} />
        </>
      }
    >
      <ItemDetailsContent
        exportType={exportSettings.exportType}
        symbols={symbols}
        onClearAll={handleClearAllClick}
      />
    </DataHubCard>
  );
};

export default ItemDetailsSection;
